import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/pro-solid-svg-icons";

import { T } from "@repo/transifex";
import { priceFormatter } from "~/utils/price-formatter";
import { type UserCollectLimit, type Currency } from "~/api/products/types";
import { type resolveBasketTaxTotals } from "~/lib/basket-tax-totals";

import { Price } from "./price";

type TaxTotals = NonNullable<ReturnType<typeof resolveBasketTaxTotals>>;

type Localisation = {
  currency: Currency;
  locale: string;
  vatLabel: string;
};

type PriceOverviewProps = {
  unitPriceSubTotalInclVat: number;
  /** Basket total after product discounts, hybrid / unit-price allowances, and order discount (minor units, incl. VAT). */
  unitPriceTotalInclVat: number;
  unitPriceDiscountInclVat: number;
  unitPriceBalanceToPayInclVat: number;
  unitPriceSplitCashInclVat: number;
  unitPriceTotalExclVat: number;
  /** Deposit charged across the basket. Never reduced by a discount. */
  depositTotalInclVat: number;
  /** Same deposit net of VAT. Equal to `depositTotalInclVat` for a no-VAT scheme. */
  depositTotalExclVat: number;
  /** Per-rate tax rows, or null when prices show incl VAT or nothing is taxed. */
  taxTotals: TaxTotals | null;
  vatLabel: string;

  collectLimit: UserCollectLimit | null;

  locale: string;
  currency: Currency;

  showPricesInclVat: boolean;
};

export const PriceOverview = ({
  unitPriceSubTotalInclVat,
  unitPriceTotalInclVat,
  unitPriceDiscountInclVat,
  unitPriceBalanceToPayInclVat,
  unitPriceSplitCashInclVat,
  unitPriceTotalExclVat,
  depositTotalInclVat,
  depositTotalExclVat,
  taxTotals,
  collectLimit,
  locale,
  currency,
  showPricesInclVat,
  vatLabel
}: PriceOverviewProps) => {
  let unitPriceToPayInclVat =
    unitPriceTotalInclVat - (unitPriceSplitCashInclVat ?? 0);

  const canFullyPayWithBalance =
    unitPriceBalanceToPayInclVat &&
    unitPriceBalanceToPayInclVat === unitPriceToPayInclVat;

  if (!canFullyPayWithBalance) {
    unitPriceToPayInclVat -= unitPriceBalanceToPayInclVat;
  }

  const isPriceMatched = unitPriceToPayInclVat === unitPriceSubTotalInclVat;

  const showDailyBalanceLimitWarning =
    collectLimit &&
    collectLimit.unitAvailableInclVat - unitPriceBalanceToPayInclVat <= 0;

  const localisation: Localisation = { currency, locale, vatLabel };

  return (
    <div className="mx-4 flex flex-col gap-3 border-t border-divider-main py-3">
      <ExclVatSubtotal
        taxTotals={taxTotals}
        showPricesInclVat={showPricesInclVat}
        unitPriceTotalExclVat={unitPriceTotalExclVat}
        unitPriceToPayInclVat={unitPriceToPayInclVat}
        localisation={localisation}
      />

      {/* The excl breakdown already states the basket, so an incl subtotal
          beside it would be two totals on different bases. */}
      {taxTotals || isPriceMatched ? null : (
        <Price
          title={<T _str="Total" />}
          type="secondary"
          value={priceFormatter({
            value: unitPriceSubTotalInclVat,
            currency,
            locale
          })}
        />
      )}

      {unitPriceDiscountInclVat === 0 ? null : (
        <Price
          title={<T _str="Discount" />}
          type="tertiary"
          value={priceFormatter({
            value: -unitPriceDiscountInclVat,
            currency,
            locale
          })}
        />
      )}

      <DepositRow
        deposit={showPricesInclVat ? depositTotalInclVat : depositTotalExclVat}
        localisation={localisation}
      />

      <TaxLines taxTotals={taxTotals} localisation={localisation} />

      {unitPriceBalanceToPayInclVat === 0 || canFullyPayWithBalance ? null : (
        <Price
          title={
            <div className="flex items-center gap-2.5">
              <T _str="Balance" />
            </div>
          }
          type="tertiary"
          value={priceFormatter({
            value: -unitPriceBalanceToPayInclVat,
            currency,
            locale
          })}
        />
      )}

      {unitPriceSplitCashInclVat === 0 ? null : (
        <Price
          title={<T _str="Cash" />}
          type="tertiary"
          value={priceFormatter({
            value: -unitPriceSplitCashInclVat,
            currency,
            locale
          })}
        />
      )}

      <Price
        title={
          <div className="flex items-center gap-4">
            <T _str="To pay" />

            {showDailyBalanceLimitWarning ? (
              <span className="flex items-center gap-1 text-sm font-medium text-interface-negative-2">
                <FontAwesomeIcon icon={faWarning} />
                <T
                  _str="Daily {amount} balance limit reached"
                  amount={priceFormatter({
                    value: collectLimit.unitLimitInclVat,
                    currency,
                    locale
                  })}
                />
              </span>
            ) : null}
          </div>
        }
        type="primary"
        value={priceFormatter({
          value: unitPriceToPayInclVat,
          currency,
          locale
        })}
      />
    </div>
  );
};

type ExclVatSubtotalProps = {
  taxTotals: TaxTotals | null;
  showPricesInclVat: boolean;
  unitPriceTotalExclVat: number;
  unitPriceToPayInclVat: number;
  localisation: Localisation;
};

/**
 * The excl subtotal. With tax rows it is product money only, so that it plus
 * the deposit plus the tax rows reconcile to what is paid.
 */
const ExclVatSubtotal = ({
  taxTotals,
  showPricesInclVat,
  unitPriceTotalExclVat,
  unitPriceToPayInclVat,
  localisation: { currency, locale, vatLabel }
}: ExclVatSubtotalProps) => {
  if (taxTotals) {
    return (
      <Price
        title={<T _str="Subtotal (excl. {vatLabel})" vatLabel={vatLabel} />}
        type="secondary"
        value={priceFormatter({
          value: taxTotals.totalExclVat,
          currency,
          locale
        })}
      />
    );
  }

  if (showPricesInclVat || unitPriceTotalExclVat === unitPriceToPayInclVat) {
    return null;
  }

  return (
    <Price
      title={<T _str="Total excl. {vatLabel}" vatLabel={vatLabel} />}
      type="secondary"
      value={priceFormatter({ value: unitPriceTotalExclVat, currency, locale })}
    />
  );
};

type DepositRowProps = {
  deposit: number;
  localisation: Localisation;
};

const DepositRow = ({
  deposit,
  localisation: { currency, locale }
}: DepositRowProps) => {
  if (deposit === 0) return null;

  return (
    <Price
      title={<T _str="Deposit Fees" />}
      type="tertiary"
      value={priceFormatter({
        value: deposit,
        currency,
        locale,
        options: { signDisplay: "exceptZero" }
      })}
    />
  );
};

type TaxLinesProps = {
  taxTotals: TaxTotals | null;
  localisation: Localisation;
};

const TaxLines = ({
  taxTotals,
  localisation: { currency, locale, vatLabel }
}: TaxLinesProps) => {
  if (!taxTotals) return null;

  return taxTotals.taxLines.map((line) => (
    <Price
      key={line.rate}
      title={
        <T
          _str="{vatLabel} ({rate}%)"
          vatLabel={vatLabel}
          rate={(line.rate * 100).toFixed(2)}
        />
      }
      type="tertiary"
      value={priceFormatter({ value: line.taxAmount, currency, locale })}
    />
  ));
};
